import { create } from 'zustand'

import type { Conversation, ConversationMessage } from '@/types/conversation'

interface StreamingMessage {
  id: string
  content: string
  sessionId: string
}

interface ConversationState {
  conversations: Record<string, Conversation>
  messages: Record<string, ConversationMessage[]>
  streamingMessages: Record<string, StreamingMessage>
  updateConversation: (conversation: Conversation) => void
  addMessage: (conversationId: string, message: ConversationMessage) => void
  appendStreamChunk: (sessionId: string, chunk: string, messageId: string) => void
  removeConversation: (conversationId: string) => void
  reset: () => void
}

export const useRealtimeStore = create<ConversationState>((set) => ({
  conversations: {},
  messages: {},
  streamingMessages: {},
  updateConversation: (conversation) =>
    set((state) => ({
      conversations: {
        ...state.conversations,
        [conversation.id]: {
          ...state.conversations[conversation.id],
          ...conversation,
        },
      },
    })),
  addMessage: (conversationId, message) =>
    set((state) => {
      // Clear any streaming message for this conversation when final message arrives
      const updatedStreaming = { ...state.streamingMessages }
      delete updatedStreaming[conversationId]

      return {
        messages: {
          ...state.messages,
          [conversationId]: [
            ...(state.messages[conversationId] ?? []).filter(
              (existingMessage) => existingMessage.id !== message.id,
            ),
            message,
          ],
        },
        streamingMessages: updatedStreaming,
      }
    }),
  appendStreamChunk: (sessionId, chunk, messageId) =>
    set((state) => {
      const existing = state.streamingMessages[sessionId]
      return {
        streamingMessages: {
          ...state.streamingMessages,
          [sessionId]: {
            id: messageId,
            sessionId,
            content: (existing?.content ?? '') + chunk,
          },
        },
      }
    }),
  removeConversation: (conversationId) =>
    set((state) => {
      const updatedConversations = { ...state.conversations }
      const updatedMessages = { ...state.messages }
      const updatedStreaming = { ...state.streamingMessages }

      delete updatedConversations[conversationId]
      delete updatedMessages[conversationId]
      delete updatedStreaming[conversationId]

      return {
        conversations: updatedConversations,
        messages: updatedMessages,
        streamingMessages: updatedStreaming,
      }
    }),
  reset: () =>
    set(() => ({
      conversations: {},
      messages: {},
      streamingMessages: {},
    })),
}))
